import { Check, ChevronDown, Search } from 'lucide-react';
import {
  type ReactNode,
  type SelectHTMLAttributes,
  forwardRef,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';

import { Field, inputClasses } from './Input';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Вторая строка в поиске: роль человека, баланс кошелька. */
  hint?: string;
}

export type SelectSize = 'sm' | 'md';

const SIZES: Record<SelectSize, string> = {
  sm: 'h-8 py-0 pl-2.5 pr-8 text-[12.5px]',
  md: 'py-2 pl-3 pr-9 text-sm',
};

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children' | 'size'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  options: SelectOption[];
  placeholder?: string;
  /** Компактный вариант для панелей и таблиц. Размер задаётся здесь, а не классами. */
  size?: SelectSize;
  /** Что показать, когда выбирать не из чего. */
  emptyLabel?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, options, placeholder, className, required, id, size = 'md', emptyLabel, ...props },
  ref,
) {
  const { t } = useTranslation('common');
  const generatedId = useId();
  const selectId = id ?? generatedId;

  // Пустой список выглядит сломанным: показываем причину и не даём его открыть.
  const isEmpty = options.length === 0;

  const field = (
    <div className="relative">
      <select
        ref={ref}
        id={selectId}
        aria-invalid={error ? true : undefined}
        disabled={props.disabled || isEmpty}
        className={cn(
          inputClasses(Boolean(error), 'appearance-none cursor-pointer truncate'),
          SIZES[size],
          className,
        )}
        {...props}
      >
        {isEmpty ? (
          <option value="">{emptyLabel ?? t('nothingToPick')}</option>
        ) : (
          <>
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                // Часть систем рисует список средствами ОС и игнорирует тему
                // страницы — цвета на самих пунктах спасают тёмную тему.
                className="bg-surface text-text"
              >
                {option.label}
              </option>
            ))}
          </>
        )}
      </select>

      <ChevronDown
        className={cn(
          'pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-subtle',
          size === 'sm' ? 'right-2.5' : 'right-3',
        )}
        aria-hidden
      />
    </div>
  );

  if (!label && !hint && !error) return field;

  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={selectId}>
      {field}
    </Field>
  );
});

/**
 * Список с поиском.
 *
 * Нужен там, где вариантов десятки: люди, кошельки, категории. В обычном
 * `<select>` из тридцати человек нужного ищут прокруткой, и это единственное
 * место, где интерфейс заметно мешает работать.
 */
export function SearchableSelect({
  value,
  onChange,
  options,
  label,
  hint,
  error,
  placeholder,
  emptyLabel,
  required,
  disabled,
  className,
  /** Ниже этого числа вариантов поиск только мешает — рисуем обычный список. */
  searchThreshold = 8,
}: {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  placeholder?: string;
  emptyLabel?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  searchThreshold?: number;
}) {
  const { t } = useTranslation('common');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);

  const selected = options.find((option) => option.value === value) ?? null;

  const filtered = useMemo(() => {
    const text = query.trim().toLowerCase();
    if (!text) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(text) || option.hint?.toLowerCase().includes(text),
    );
  }, [options, query]);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlight(Math.max(0, filtered.findIndex((option) => option.value === value)));
      // Фокус в поле поиска: список открывают, чтобы искать.
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Мало вариантов — обычный список надёжнее: он нативный и работает везде.
  if (options.length < searchThreshold) {
    return (
      <Select
        label={label}
        hint={hint}
        error={error}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        emptyLabel={emptyLabel}
        options={options}
        value={value}
        className={className}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  function pick(option: SelectOption) {
    if (option.disabled) return;
    onChange(option.value);
    setOpen(false);
  }

  const control = (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((previous) => !previous)}
        className={cn(
          inputClasses(Boolean(error), 'flex items-center gap-2 py-2 pl-3 pr-9 text-left'),
          className,
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate', !selected && 'text-subtle')}>
          {selected?.label ?? placeholder ?? t('choose')}
        </span>
        <ChevronDown className="absolute right-3 h-4 w-4 shrink-0 text-subtle" aria-hidden />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-[10px] border border-border bg-surface shadow-lg">
          <div className="flex items-center gap-2 border-b border-border px-2.5 py-2">
            <Search className="h-3.5 w-3.5 shrink-0 text-subtle" aria-hidden />
            <input
              ref={inputRef}
              value={query}
              placeholder={t('search')}
              className="w-full bg-transparent text-[13px] outline-none placeholder:text-subtle"
              onChange={(event) => {
                setQuery(event.target.value);
                setHighlight(0);
              }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  setHighlight((index) => Math.min(index + 1, filtered.length - 1));
                } else if (event.key === 'ArrowUp') {
                  event.preventDefault();
                  setHighlight((index) => Math.max(index - 1, 0));
                } else if (event.key === 'Enter') {
                  event.preventDefault();
                  const option = filtered[highlight];
                  if (option) pick(option);
                } else if (event.key === 'Escape') {
                  setOpen(false);
                }
              }}
            />
          </div>

          <ul role="listbox" className="max-h-[240px] overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-[13px] text-muted">{t('nothingFound')}</li>
            ) : (
              filtered.map((option, index) => (
                <li key={option.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    disabled={option.disabled}
                    onMouseEnter={() => setHighlight(index)}
                    onClick={() => pick(option)}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] disabled:opacity-50',
                      index === highlight && 'bg-surface-2',
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{option.label}</span>
                      {option.hint && (
                        <span className="block truncate text-[11.5px] text-subtle">
                          {option.hint}
                        </span>
                      )}
                    </span>
                    {option.value === value && (
                      <Check className="h-3.5 w-3.5 shrink-0 text-accent" aria-hidden />
                    )}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );

  if (!label && !hint && !error) return control;

  return (
    <Field label={label} hint={hint} error={error} required={required}>
      {control}
    </Field>
  );
}

/** Переключатель из нескольких вариантов — читается быстрее выпадающего списка. */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  label,
  hint,
  className,
  columns,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: ReactNode; description?: ReactNode }[];
  label?: ReactNode;
  hint?: ReactNode;
  className?: string;
  columns?: number;
}) {
  const control = (
    <div
      role="group"
      className={cn('grid gap-1 rounded-[10px] border border-border bg-surface-2 p-1', className)}
      style={{ gridTemplateColumns: `repeat(${columns ?? options.length}, minmax(0, 1fr))` }}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded-lg px-2.5 py-2 text-[13px] leading-tight transition-colors duration-150',
              active
                ? 'bg-surface font-semibold text-text shadow-card'
                : 'font-medium text-muted hover:text-text',
            )}
          >
            <span className="block">{option.label}</span>
            {option.description && (
              <span className="mt-0.5 block text-[11px] font-normal opacity-70">
                {option.description}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  if (!label && !hint) return control;

  return (
    <Field label={label} hint={hint}>
      {control}
    </Field>
  );
}
